import React, { useEffect, useRef, useState } from "react";
import "./RechargeWell.css";

// Gallery image imports — replace with your actual image files
import gallery1 from "../assets/gallery/Rgaller1.jpeg";
import gallery2 from "../assets/gallery/Rgaller2.jpeg";
import gallery3 from "../assets/gallery/Rgaller3.jpeg";
import gallery4 from "../assets/gallery/Rgaller4.jpeg";
import gallery5 from "../assets/gallery/Rgaller5.jpeg";
import gallery6 from "../assets/gallery/Rgaller6.jpeg";
import gallery7 from "../assets/gallery/Rgaller7.jpeg";
import gallery8 from "../assets/gallery/Rgaller8.jpeg";
import gallery9 from "../assets/gallery/Rgaller9.jpeg";
import overviewImg from "../assets/gallery/rw-gallery-1.jpeg";

const GALLERY_IMAGES = [
  { src: gallery1 },
  { src: gallery2 },
  { src: gallery3},
  { src: gallery4 },
  { src: gallery5 },
  { src: gallery6 },
  { src: gallery7 },
  { src: gallery8 },
];

/* ── Main RechargeWell Component ── */
const RechargeWell = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach((key) => {
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.15 }
      );
      if (sectionRefs.current[key]) {
        observers[key].observe(sectionRefs.current[key]);
      }
    });
    return () => {
      Object.values(observers).forEach((obs) => obs.disconnect());
    };
  }, []);

  const setRef = (key) => (el) => {
    sectionRefs.current[key] = el;
  };

  const methodsData = [
    {
      number: "01",
      title: "Site Assessment & Design",
      desc: "Hydrogeological assessment to determine aquifer suitability, recharge capacity, and optimal well placement for effective groundwater replenishment.",
      icon: "🗺️",
    },
    {
      number: "02",
      title: "Borehole Construction",
      desc: "Precision rotary drilling to design depth with proper casing installation to ensure structural integrity and prevent contamination.",
      icon: "⬇",
    },
    {
      number: "03",
      title: "Screen & Filter Pack",
      desc: "Installation of stainless steel well screens and gravel filter packs designed to maximise recharge rates while preventing sediment ingress.",
      icon: "🔩",
    },
    {
      number: "04",
      title: "Well Development",
      desc: "Surging and air-lifting to remove drilling fines, develop the filter pack, and achieve design recharge capacity before commissioning.",
      icon: "💧",
    },
    {
      number: "05",
      title: "Pumping & Recharge Testing",
      desc: "Step and constant rate testing to determine aquifer transmissivity, storage coefficient, and sustainable recharge performance.",
      icon: "📈",
    },
    {
      number: "06",
      title: "Monitoring & Reporting",
      desc: "Groundwater level monitoring, water quality sampling, and comprehensive reporting for regulatory compliance and performance tracking.",
      icon: "📋",
    },
  ];

  return (
    <div className="rw-page" id="#recharge-well">
      {/* Top Banner */}
      <section className="rw-top-banner">
        <div className="rw-top-banner__grid" />
        <div className="rw-top-banner__glow" />
        <h1 className="rw-top-banner__title">
          Recharge <span className="rw-text-accent">Well</span>
        </h1>
      </section>

      {/* Overview Section */}
      <section
        className={`rw-overview ${isVisible["overview"] ? "rw-animate-in" : ""}`}
        ref={setRef("overview")}
      >
        <div className="rw-overview__inner">
          <div className="rw-overview__left">
            <div className="rw-section-label">
              <span className="rw-section-label__line" />
              WHAT WE DO
            </div>
            <h2 className="rw-section-title">
              REPLENISHING THE
              <br />
              <span className="rw-text-accent">AQUIFER BELOW</span>
            </h2>
            <p className="rw-overview__text">
              Recharge wells are a critical groundwater management solution — designed to
              return water to the aquifer safely and efficiently. GSK Geotech's Recharge
              Well service covers the full cycle: from hydrogeological assessment and
              borehole construction to well development, testing, and long-term monitoring.
            </p>
            <p className="rw-overview__text">
              Our experienced team has delivered recharge well installations across
              dewatering mitigation, stormwater recharge, and sustainable groundwater
              management projects for LTA, PUB, and private development clients under leading main contracters throughout
              Singapore.
            </p>
            <div className="rw-overview__highlights">
              {[
                "Recharge well construction",
                "Compliant with regulations",
                "Testing and reporting",
                "Monitoring",
              ].map((item, i) => (
                <div key={i} className="rw-overview__highlight-item">
                  <span className="rw-overview__highlight-icon">✦</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="rw-overview__right">
            <div className="rw-overview__image-block">
              <img
                src={overviewImg}
                alt="Recharge Well Groundwater Replenishment Works"
                className="rw-overview__image"
              />
              <div className="rw-overview__stat-card">
                <span className="rw-overview__stat-number">13+</span>
                <span className="rw-overview__stat-text">RECHARGE WELLS<br />INSTALLED</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section
        className={`rw-methods ${isVisible["methods"] ? "rw-animate-in" : ""}`}
        ref={setRef("methods")}
      >
        <div className="rw-methods__inner">
          <div className="rw-section-label">
            <span className="rw-section-label__line" />
            OUR APPROACH
          </div>
          <h2 className="rw-section-title">
            CONSTRUCTION
            <br />
            <span className="rw-text-accent">METHODS</span>
          </h2>
          <p className="rw-methods__subtitle">
            From aquifer assessment to long-term monitoring — we deploy proven recharge
            well techniques tailored to each site's hydrogeological conditions.
          </p>
          <div className="rw-methods__grid">
            {methodsData.map((method, i) => (
              <div
                key={i}
                className="rw-method-card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="rw-method-card__number">{method.number}</div>
                <div className="rw-method-card__icon">{method.icon}</div>
                <h3 className="rw-method-card__title">{method.title}</h3>
                <p className="rw-method-card__desc">{method.desc}</p>
                <div className="rw-method-card__line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        className={`rw-process ${isVisible["process"] ? "rw-animate-in" : ""}`}
        ref={setRef("process")}
      >
        <div className="rw-process__inner">
          <div className="rw-section-label">
            <span className="rw-section-label__line" />
            HOW WE WORK
          </div>
          <h2 className="rw-section-title">
            OUR <span className="rw-text-accent">PROCESS</span>
          </h2>
          <div className="rw-process__steps">
            {[
              {
                step: "01",
                title: "Hydrogeological Assessment",
                desc: "Review of existing borehole data, geological maps, and site conditions to confirm aquifer suitability and design well specification.",
              },
              {
                step: "02",
                title: "Drilling & Casing",
                desc: "Rotary drilling to target aquifer depth with temporary and permanent casing installation for wellbore stability.",
              },
              {
                step: "03",
                title: "Screen & Gravel Pack",
                desc: "Precision placement of well screens at the productive zone with carefully graded gravel filter pack to maximise inflow performance.",
              },
              {
                step: "04",
                title: "Well Development & Testing",
                desc: "Surging, jetting and step-rate testing to develop the well and determine its sustainable recharge capacity.",
              },
              {
                step: "05",
                title: "Commissioning & Handover",
                desc: "Wellhead completion, pump installation (if required), baseline water quality sampling, and full as-built documentation for client handover.",
              },
            ].map((item, i) => (
              <div key={i} className="rw-process__step">
                <div className="rw-process__step-num">{item.step}</div>
                <div className="rw-process__step-connector" />
                <div className="rw-process__step-content">
                  <h4 className="rw-process__step-title">{item.title}</h4>
                  <p className="rw-process__step-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section
        className={`rw-projects ${isVisible["projects"] ? "rw-animate-in" : ""}`}
        ref={setRef("projects")}
      >
        <div className="rw-projects__inner">
          <div className="rw-section-label">
            <span className="rw-section-label__line" />
            TRACK RECORD
          </div>
          <h2 className="rw-section-title">
            COMPLETED RECHARGE
            <br />
            <span className="rw-text-accent">WELL PROJECTS</span>
          </h2>
          <div className="rw-projects__grid">
            {[
              { title: "Drilling Work for Recharge Well for N105", client: "Knights Synergy (S) Pte Ltd" },
              { title: "Recharge Well Link Sewers LK11C4", client: "MK Tunnel Pte Ltd" },
              { title: "Recharge Well Work at DTSS Phase 2 Link Sewers Schedule 2 Contract 3B", client: "Megastones Holdings Pte Ltd" },
              { title: "Recharge Well at CR 102", client: "Geo Motion Singapore" },
              { title: "Recharge Well at DTSS Mh11 LK11C4", client: "MK Tunnel Pte Ltd" },
              { title: "Recharge Well Works for Link Sewer MH09 LK11C4", client: "MK Tunnel" },
              { title: "Recharge Well at CR 106", client: "Geo Motion Singapore" },
              { title: "Recharge Well at CR 107", client: "Geo Motion Singapore" },
              { title: "Recharge Well at N106", client: "Geo Motion Singapore" },
              { title: "Drilling and Installation of Recharge Wells CR106/N106", client: "Geo Motion Singapore" },
            ].map((proj, i) => (
              <div key={i} className="rw-project-card">
                <h4 className="rw-project-card__title">{proj.title}</h4>
                <p className="rw-project-card__client">{proj.client}</p>
              </div>
            ))}
          </div>
          <div className="rw-projects__cta">
            <a href="/projects" className="rw-btn rw-btn--outline">
              VIEW ALL PROJECTS →
            </a>
          </div>
        </div>
      </section>

      {/* Masonry Gallery */}
      <section className="rw-gallery">
        <div className="rw-gallery__inner">
          <div className="rw-section-label">
            <span className="rw-section-label__line" />
            SITE PHOTOS
          </div>
          <h2 className="rw-section-title">
            PROJECT <span className="rw-text-accent">GALLERY</span>
          </h2>
          <div className="rw-gallery__masonry">
            {GALLERY_IMAGES.map((img, i) => (
              <div key={i} className="rw-gallery__item">
                <img src={img.src} alt={img.caption} className="rw-gallery__img" />
                <div className="rw-gallery__overlay">
                  <span className="rw-gallery__caption">{img.caption}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="rw-cta">
        <div className="rw-cta__inner">
          <div className="rw-cta__left">
            <p className="rw-cta__label">READY TO START?</p>
            <h2 className="rw-cta__title">
              NEED A RECHARGE WELL
              <br />
              <span className="rw-text-accent">FOR YOUR PROJECT?</span>
            </h2>
            <p className="rw-cta__desc">
              Our team is ready to mobilise. Get in touch for a detailed quote
              and consultation tailored to your project's hydrogeological requirements.
            </p>
          </div>
          <div className="rw-cta__right">
            <a href="/contact-us" className="rw-btn rw-btn--primary rw-btn--large">
              CONTACT US →
            </a>
            <div className="rw-cta__contacts">
              <span>Nedu: +65 9239 8494</span>
              <span>Suben: +65 9027 1160</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RechargeWell;